import React from "react";
import styled from "styled-components";
import { palette } from "@/styles/palette";
import FlavorSelector from "./FlavorSelector";
import { AromaCategory } from "@/types";
import { useNavigate } from "react-router-dom";
import { fontFamily } from "@/styles/font";

interface ProductInfo {
    name: string;
    thumbnailImageUrl?: string;
    type: string;
    abv: number;
    breweryName: string;
    productAltKey?: string; // 상품 상세 페이지로 이동하기 위한 키 추가
}

interface ReviewDetailProps {
    productInfo: ProductInfo;
    rating: number;
    reviewDate: string;
    content: string;
    tasteValues: {
        sweetness: number;
        sourness: number;
        freshness: number;
        thickness: number;
    };
    selectedTags: number[];
    aromaTags: AromaCategory[];
    reviewImages?: string[];
    userNickname?: string;
}

const ReviewDetail: React.FC<ReviewDetailProps> = ({
    productInfo,
    rating,
    reviewDate,
    content,
    tasteValues,
    selectedTags,
    aromaTags,
    reviewImages,
    userNickname
}) => {
    const navigate = useNavigate();

    const getRatingText = (rating: number) => {
        switch (rating) {
            case 5:
                return "다시 마시고 싶어요";
            case 4:
                return "맛있어요";
            case 3:
                return "보통이에요";
            case 2:
                return "별로예요";
            case 1:
                return "다시는 마시지 않을래요";
            default:
                return "";
        }
    };

    const formatDate = (dateString: string) => {
        try {
            const date = new globalThis.Date(dateString);
            return `${date.getFullYear()}. ${String(date.getMonth() + 1).padStart(2, "0")}. ${String(date.getDate()).padStart(2, "0")}`;
        } catch (error) {
            console.error("날짜 변환 오류:", error);
            return dateString;
        }
    };

    // 선택된 아로마 태그 찾기
    const selectedAromaTags = aromaTags
        .flatMap((category) => category.aromaList)
        .filter((aroma) => selectedTags.includes(aroma.id))
        .map((aroma) => aroma.name);

    // 상품 상세 페이지로 이동하는 함수
    const handleProductClick = () => {
        if (productInfo.productAltKey) {
            navigate(`/alcohol/${productInfo.productAltKey}`);
        }
    };

    return (
        <Container>
            {/* 제품 이미지 및 정보 */}
            <ProductContainer>
                <ProductImage
                    src={productInfo.thumbnailImageUrl || "/images/default-image.png"}
                    alt={productInfo.name}
                    onClick={productInfo.productAltKey ? handleProductClick : undefined}
                    style={{ cursor: productInfo.productAltKey ? "pointer" : "default" }}
                    onError={(e) => {
                        const target = e.target as HTMLImageElement;
                        target.onerror = null; // 무한 루프 방지
                        target.src = "/images/default-image.png";
                    }}
                />
            </ProductContainer>

            <Content>
                {/* 날짜 정보 */}
                <DateContainer>
                    <Date>{formatDate(reviewDate)}</Date>
                </DateContainer>

                {/* 별점 평가 섹션 */}
                <Section>
                    <ProductTitleContainer onClick={productInfo.productAltKey ? handleProductClick : undefined}>
                        <ProductTitle hasLink={!!productInfo.productAltKey}>{productInfo.name}</ProductTitle>
                        {productInfo.productAltKey && <LinkIcon src="/icon/chevron-right.png" alt="상품 상세로 이동" />}
                    </ProductTitleContainer>
                    <SectionContent>
                        <RatingSection>
                            <RatingStars>
                                {[1, 2, 3, 4, 5].map((star) => (
                                    <StarIcon
                                        key={star}
                                        src="/icon/star.png"
                                        alt={`별점 ${star}점`}
                                        $isActive={star <= rating}
                                    />
                                ))}
                            </RatingStars>
                            <RatingText>
                                {rating}점 / {getRatingText(rating)}
                            </RatingText>
                        </RatingSection>
                    </SectionContent>
                </Section>

                {/* 맛 평가 섹션 */}
                <Section>
                    <SectionTitle>이런 맛이었어요</SectionTitle>
                    <SectionContent>
                        <FlavorSelector tasteValues={tasteValues} readOnly={true} />
                    </SectionContent>
                </Section>

                {/* 상세 리뷰 섹션 */}
                <Section>
                    <SectionTitle>마신 후 이야기</SectionTitle>
                    <SectionContent>
                        <ReviewText>{content}</ReviewText>
                    </SectionContent>
                </Section>

                {/* 태그 섹션 */}
                {selectedAromaTags.length > 0 && (
                    <Section>
                        <SectionTitle>이런 향과 맛이 느껴졌어요</SectionTitle>
                        <SubText>선택한 향과 맛 태그들이에요</SubText>
                        <SectionContent>
                            <TagList>
                                {selectedAromaTags.map((tag, index) => (
                                    <Tag key={index}>{tag}</Tag>
                                ))}
                            </TagList>
                        </SectionContent>
                    </Section>
                )}
            </Content>
        </Container>
    );
};

export default ReviewDetail;

const Container = styled.div`
    width: 100%;
    background-color: ${palette.white};
    padding-bottom: 32px;
`;

const ProductContainer = styled.div`
    position: relative;
    width: 100%;
    display: flex;
    justify-content: center;
    padding: 20px 0;
`;

const ProductImage = styled.img`
    width: auto;
    height: 200px;
    object-fit: contain;
    border-radius: 8px;
`;

const BreweryBadge = styled.div`
    position: absolute;
    top: 20px;
    left: 20px;
    display: flex;
    align-items: center;
    gap: 4px;
    padding: 4px 8px;
    background-color: ${palette.white};
    border-radius: 20px;
    font-size: 12px;
    font-weight: 500;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);

    svg {
        width: 16px;
        height: 16px;
    }

    span {
        color: ${palette.gray[800]};
    }
`;

const Content = styled.main`
    padding: 24px 16px;
    display: flex;
    flex-direction: column;
    align-items: center;
    margin-top: -20px;
`;

const Section = styled.section`
    box-sizing: border-box;
    width: 100%;
    max-width: 361px;
    background: #ffffff;
    box-shadow:
        0px 16px 32px -4px rgba(12, 12, 13, 0.1),
        0px 4px 4px -4px rgba(12, 12, 13, 0.05);
    border-radius: 20px;
    padding: 24px;
    margin: 0 auto 16px;
`;

const SectionTitle = styled.h2`
    display: flex;
    flex-direction: row;
    align-items: center;
    gap: 9px;
    width: 100%;
    font-family: ${fontFamily.base};
    font-style: normal;
    font-weight: 600;
    font-size: 18px;
    line-height: 24px;
    color: #000000;
    margin: 0;
`;

const SubText = styled.p`
    font-family: ${fontFamily.base};
    font-size: 14px;
    color: ${palette.gray[600]};
    margin: 8px 0 0;
`;

const SectionContent = styled.div`
    margin-top: 16px;
    width: 100%;
`;

const MetaInfo = styled.div`
    display: flex;
    justify-content: space-between;
    margin-top: 8px;
    font-size: 14px;
    color: ${palette.gray[500]};
`;

const UserName = styled.span`
    font-weight: 500;
`;

const Date = styled.span`
    font-size: 14px;
    color: ${palette.gray[600]};
    font-weight: 500;
`;

const DateContainer = styled.div`
    display: flex;
    justify-content: flex-end;
    width: 100%;
    max-width: 361px;
    margin-bottom: 8px;
    padding: 0 4px;
`;

const RatingSection = styled.div`
    display: flex;
    flex-direction: column;
    gap: 8px;
`;

const RatingStars = styled.div`
    display: flex;
    gap: 3px;
`;

const StarIcon = styled.img<{ $isActive?: boolean }>`
    width: 40px;
    height: 40px;
    opacity: ${(props) => (props.$isActive ? 1 : 0.3)};
`;

const RatingText = styled.span`
    font-family: ${fontFamily.base};
    font-style: normal;
    font-weight: 400;
    font-size: 14px;
    line-height: 17px;
    display: flex;
    align-items: center;
    color: #414141;
    margin-left: 7px;
`;

const ReviewText = styled.p`
    font-size: 16px;
    line-height: 1.6;
    color: ${palette.black};
    margin: 0 0 20px 0;
    white-space: pre-wrap;
`;

const ReviewImages = styled.div`
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
`;

const ReviewImage = styled.img`
    width: 100%;
    max-width: 300px;
    height: auto;
    object-fit: cover;
    border-radius: 8px;
`;

const TagList = styled.div`
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
`;

const Tag = styled.span`
    display: inline-flex;
    padding: 6px 12px;
    background-color: ${palette.primary};
    color: ${palette.white};
    border-radius: 10px;
    font-size: 14px;
`;

const ProductInfoTable = styled.div`
    display: flex;
    flex-direction: column;
    width: 100%;
`;

const ProductInfoRow = styled.div`
    display: flex;
    padding: 12px 0;
    border-bottom: 1px solid ${palette.gray[200]};

    &:last-child {
        border-bottom: none;
    }
`;

const ProductInfoLabel = styled.span`
    flex: 1;
    font-size: 14px;
    color: ${palette.gray[700]};
`;

const ProductInfoValue = styled.span`
    flex: 2;
    font-size: 14px;
    color: ${palette.black};
    font-weight: 500;
`;

const ProductTitleContainer = styled.div<{ onClick?: () => void }>`
    display: flex;
    flex-direction: row;
    align-items: center;
    gap: 8px;
    width: 100%;
    cursor: ${(props) => (props.onClick ? "pointer" : "default")};
    transition: opacity 0.2s ease;

    &:hover {
        opacity: ${(props) => (props.onClick ? 0.8 : 1)};
    }
`;

const ProductTitle = styled(SectionTitle)<{ hasLink: boolean }>`
    color: ${(props) => (props.hasLink ? palette.primary : "#000000")};
    text-decoration: ${(props) => (props.hasLink ? "underline" : "none")};
    text-underline-offset: 4px;
    text-decoration-thickness: 1px;
`;

const LinkIcon = styled.img`
    width: 16px;
    height: 16px;
`;

const ProductName = styled.h1<{ hasLink: boolean }>`
    font-family: ${fontFamily.base};
    font-weight: 600;
    font-size: 24px;
    line-height: 32px;
    color: ${(props) => (props.hasLink ? palette.primary : palette.black)};
    margin: 0;
    text-decoration: ${(props) => (props.hasLink ? "underline" : "none")};
    text-underline-offset: 4px;
    text-decoration-thickness: 1px;
`;
